import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';

import { cn } from '../lib/cn';

const buttonStyles = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
    'transition-[color,background-color,border-color,box-shadow,transform] duration-150',
    'active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100',
    // Visible focus is not decoration. Somebody navigating this by keyboard
    // needs to know where they are.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        // primary-600 under white text clears WCAG AA (see styles-contrast.spec).
        primary:
          'bg-primary-600 text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 hover:shadow-md hover:shadow-primary-600/25 focus-visible:ring-primary-500',
        secondary:
          'border border-slate-200 bg-white text-slate-800 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-primary-500',
        destructive:
          'bg-red-600 text-white shadow-sm shadow-red-600/20 hover:bg-red-700 focus-visible:ring-red-600',
        ghost:
          'text-slate-700 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-primary-500',
      },
      size: {
        // The default is generous on purpose: this is operated from a phone,
        // in a vehicle, sometimes with gloves on.
        md: 'h-11 px-4 text-sm',
        sm: 'h-9 px-3 text-sm',
        lg: 'h-13 px-6 text-base',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  loading?: boolean;
  /** Decorative: aria-hidden. An icon-only button still needs a name —
   * Button never infers an aria-label from the icon. */
  icon?: LucideIcon;
  iconPosition?: 'start' | 'end';
  children: ReactNode;
}

export function Button({
  variant,
  size,
  loading = false,
  disabled = false,
  icon: Icon,
  iconPosition = 'start',
  className,
  type = 'button',
  children,
  ...props
}: ButtonProps): JSX.Element {
  // A busy button shows one indicator, never two: the spinner takes the
  // icon's place instead of sitting beside it.
  const iconElement = loading ? (
    <Spinner />
  ) : Icon === undefined ? null : (
    <Icon aria-hidden="true" className="size-4" />
  );

  return (
    <button
      // Defaults to button, never submit. An unspecified button inside a form
      // submits it, and that surprise has caused more accidental submissions
      // than any other default in HTML.
      type={type}
      // A busy button that still accepts clicks turns one slow confirmation
      // into three orders.
      disabled={disabled || loading}
      aria-busy={loading}
      className={cn(buttonStyles({ variant, size }), className)}
      {...props}
    >
      {iconPosition === 'start' ? iconElement : null}
      {children}
      {iconPosition === 'end' ? iconElement : null}
    </button>
  );
}

function Spinner(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
